import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, Outlet } from 'react-router';
import { getCurrentSafetyRole, type AppRole } from './currentRole';

export interface RequireRoleProps {
  /** Allowed Safety app roles. Caller is granted access if their role is in this set. */
  roles: AppRole[];
  /**
   * Where to send the user when their role isn't allowed. Defaults to
   * the dashboard — every authenticated user can see that, so it's the
   * safest "you can't be here, go somewhere you can" target.
   */
  redirectTo?: string;
}

/**
 * Route-level role guard. Renders an `<Outlet />` for nested
 * `<Route>`s when the caller's Safety app role is in the allowed set;
 * otherwise redirects to `redirectTo`.
 *
 * Gates on the **Safety app role** (Owner / Admin / Manager / ...), not
 * the account role — Settings pages are app-specific, so being a Safety
 * Admin while only an account Member is the natural state for a
 * promoted-to-admin user.
 *
 * Defence-in-depth: the BE handlers already check authorisation and
 * reject privileged calls from lower-tier roles — this guard exists so:
 *  1. A manager who deep-links to a settings page doesn't see a
 *     half-rendered admin page that immediately errors.
 *  2. The FE never accidentally exposes admin UI to a session that
 *     shouldn't see it (e.g. a sidebar regression).
 *
 * Sits inside `RequireAuth` in the route tree, so by the time this
 * renders the user is already authenticated; we only need to check role.
 */
export function RequireRole({ roles, redirectTo = '/dashboard' }: RequireRoleProps) {
  const { user } = useAuth0();
  const role = getCurrentSafetyRole(user);
  if (!roles.includes(role)) {
    // `replace` so the disallowed URL doesn't stay in history — back
    // button after the redirect goes to where the user actually was
    // before, not back into a forbidden page.
    return <Navigate to={redirectTo} replace />;
  }
  return <Outlet />;
}
